import { useEffect, useId, useState, type ReactNode } from "react";
import { Floating, useFitted } from "./floating.js";
import { Info } from "./icons.js";
import "./info-tip.css";

/**
 * What a column means, next to the column.
 *
 * Every table here prints numbers whose definition lives in someone's head —
 * "Answers" counts answers, not citations; "Top competitor" is who leads on
 * this site, not overall. A reader who guesses wrong reads the whole table
 * wrong, and there is nowhere on the screen to check.
 *
 * Deliberately not a `title`: the browser waits about a second, renders in the
 * OS's own style, and cannot be reached by touch at all — so on a phone the
 * explanation simply does not exist.
 *
 * Opens on hover, on focus and on tap. Hover alone is a mouse-only feature, and
 * focus alone is a keyboard-only one; the button is a real button so all three
 * reach it. It holds no links, so it closes on leave without anyone needing to
 * travel into it.
 */
export function InfoTip({
  label,
  children,
}: {
  /** What this explains, for a reader who cannot see the column it sits in. */
  readonly label: string;
  /**
   * One or two plain sentences for a single column.
   *
   * A dense table takes a list instead: six columns in a half-width card have
   * no room for six buttons — measured, each one costs a heading about three
   * readable characters, and they were already ellipsed. Those carry one
   * button on the card's own heading and name the columns inside it.
   */
  readonly children: ReactNode;
}) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const id = useId();

  const open = (trigger: HTMLElement): void => {
    const rect = trigger.getBoundingClientRect();
    setAt({ top: rect.bottom + 6, left: rect.left });
  };

  useEffect(() => {
    if (at === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setAt(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [at]);

  return (
    <>
      <button
        type="button"
        className="infotip"
        /*
         * "About X", not "What X means": the label is sometimes plural — "the
         * Brand ranking columns" — and that sentence then disagrees with its
         * own verb. The heading is already read out, so this only has to say
         * what the button is for.
         */
        aria-label={`About ${label}`}
        aria-expanded={at !== null}
        aria-describedby={at === null ? undefined : id}
        onMouseEnter={(event) => {
          open(event.currentTarget);
        }}
        onFocus={(event) => {
          open(event.currentTarget);
        }}
        onMouseLeave={() => {
          setAt(null);
        }}
        onBlur={() => {
          setAt(null);
        }}
        onClick={(event) => {
          // Tap: there is no hover to rely on, and no link to follow.
          if (at !== null) {
            setAt(null);
            return;
          }
          open(event.currentTarget);
        }}
      >
        <Info aria-hidden="true" />
      </button>
      {at === null ? null : (
        <Floating>
          <Tip id={id} at={at}>
            {children}
          </Tip>
        </Floating>
      )}
    </>
  );
}

function Tip({
  id,
  at,
  children,
}: {
  readonly id: string;
  readonly at: { top: number; left: number };
  readonly children: ReactNode;
}) {
  const [node, fitted] = useFitted(at);
  return (
    <div
      className="infotip-card"
      id={id}
      ref={node}
      role="tooltip"
      style={{ top: `${String(fitted.top)}px`, left: `${String(fitted.left)}px` }}
    >
      {children}
    </div>
  );
}
